import React from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { usePageMeta } from "@/lib/use-page-meta";

// EDIT THESE TWO STRINGS WHEN YOU HAVE YOUR FINAL BUSINESS DETAILS
const APP_NAME = "Plainly";
const CONTACT_EMAIL = "office@myplainly.com";
const EFFECTIVE_DATE = "May 22, 2026";

export default function TermsOfService() {
  usePageMeta({
    title: "Terms of Service",
    description:
      "Plainly's terms of service — plans and pricing, acceptable use, AI output disclaimer, white-label terms, and governing law.",
    path: "/terms",
  });
  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <SiteHeader />
      <main className="max-w-3xl mx-auto px-6 md:px-8 py-12 md:py-16">
        <div className="tag-label text-[#E27D60]">Legal</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">Terms of Service</h1>
        <p className="text-sm text-[#1E392A]/55 mt-2">Effective {EFFECTIVE_DATE}</p>

        <article
          className="mt-10 space-y-8 text-[#1E392A]/85 leading-relaxed"
          data-testid="terms-of-service-content"
        >
          <section>
            <p>
              These Terms govern your use of {APP_NAME} (the "Service"). By creating an
              account or using the Service you agree to these Terms. If you don't agree,
              please don't use the Service.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl mb-2">1. The Service</h2>
            <p>
              {APP_NAME} reads aggregated metrics from your Google Analytics property (or a
              CSV you upload) and uses an AI provider to produce plain-English summaries,
              comparisons, and suggestions. We don't promise any specific business outcome
              from following AI suggestions — they're starting points, not guarantees.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl mb-2">2. Account & eligibility</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>You must be at least 18 (or the age of majority where you live) to create an account.</li>
              <li>You're responsible for maintaining the security of your Google account — that's how you authenticate.</li>
              <li>You're responsible for all activity that happens on your account.</li>
              <li>One person or business per account. Don't share login credentials.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl mb-2">3. Plans, pricing, and billing</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li><b>Free demo</b> — one free analysis on a demo dataset to try the product.</li>
              <li><b>Business ($18/month or $199/year)</b> — unlimited analyses, unlimited share links, and an <b>automatic Monday-morning email report</b> (sent every Monday from your default Google Analytics property). Monthly renews every 30 days; yearly renews every 365 days. Cancel anytime — your access stays active through the end of the paid period.</li>
              <li><b>Agency ($79/month)</b> — everything in Business plus PDF export and white-label (your company name, logo, color on shared reports). Renews every 30 days.</li>
              <li>Payments are processed by Stripe. By subscribing you authorise the recurring charge for the chosen plan.</li>
              <li>Refunds are evaluated case-by-case. If a charge looks wrong or the service didn't deliver what was advertised, email <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a> and we'll make it right.</li>
              <li>Prices may change. We'll give you at least 14 days' notice before a price change affects your subscription.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl mb-2">4. Acceptable use</h2>
            <p>Don't:</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li>Connect a Google Analytics property you don't own or aren't authorised to access.</li>
              <li>Reverse engineer, scrape, or resell the Service.</li>
              <li>Use the Service to violate any law or third-party rights.</li>
              <li>Upload malicious files or attempt to break the platform.</li>
              <li>Generate or share content that's illegal, harmful, defamatory, or that infringes someone's intellectual property.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl mb-2">5. Your data and your reports</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>You own your Google Analytics data and any CSVs you upload. We only use them as described in our <Link to="/privacy" className="underline">Privacy Policy</Link>.</li>
              <li>You own the analyses we generate for you. We grant ourselves a limited licence to store and process them in order to operate the Service.</li>
              <li>Share links are public by URL — anyone with the link can view the report. You can revoke a share link any time from the analysis page.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl mb-2">6. Google Analytics scope</h2>
            <p>
              We request the read-only Google Analytics scope (<code>analytics.readonly</code>).
              We never write to your GA account. You can revoke our access any time at{" "}
              <a
                href="https://myaccount.google.com/permissions"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                myaccount.google.com/permissions
              </a>
              {" "}or by clicking "Disconnect" inside the app.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl mb-2">7. AI output disclaimer</h2>
            <p>
              The plain-English summaries, comparisons, and suggestions are generated by an
              AI model and may be inaccurate, incomplete, or out of date. Use them as a
              starting point for your own judgement — not as a substitute for professional
              marketing, legal, or financial advice. {APP_NAME} is not responsible for
              decisions you make based on AI output.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl mb-2">8. White-label (Agency plan)</h2>
            <p>
              You may apply your own brand to share pages while on the Agency plan. You must
              own (or have the right to use) any logo, name, and colors you upload. A small
              "Powered by {APP_NAME}" attribution remains in the footer of branded share
              pages.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl mb-2">9. Termination</h2>
            <p>
              You can stop using the Service at any time. We can suspend or close accounts
              that breach these Terms, abuse the Service, or threaten the integrity of the
              platform. If we close your account other than for cause we'll refund any
              unused subscription time.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl mb-2">10. Warranty disclaimer</h2>
            <p>
              The Service is provided "as is" and "as available". To the maximum extent
              permitted by law, we disclaim all warranties — express or implied — including
              fitness for a particular purpose, merchantability, and non-infringement. We
              don't guarantee uptime or that AI output will meet your expectations.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl mb-2">11. Limitation of liability</h2>
            <p>
              To the maximum extent permitted by law, {APP_NAME}'s total liability to you
              for any claim arising out of these Terms or the Service is limited to the
              greater of (a) the amount you paid us in the 12 months before the claim, or
              (b) USD $50. We are not liable for indirect, incidental, consequential, or
              punitive damages.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl mb-2">12. Indemnity</h2>
            <p>
              You agree to defend and indemnify {APP_NAME} against claims arising from your
              breach of these Terms, your misuse of the Service, or content you submit or
              share through the Service.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl mb-2">13. Changes to these Terms</h2>
            <p>
              We may update these Terms. If a change is material we'll notify you by email
              or in-app before it takes effect. Continued use after the effective date
              means you accept the new Terms.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl mb-2">14. Governing law</h2>
            <p>
              These Terms are governed by the laws of the jurisdiction where {APP_NAME} is
              registered, without regard to conflict-of-law rules. Any dispute will be
              resolved in the courts of that jurisdiction, unless local consumer-protection
              laws give you a different right.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl mb-2">15. Contact</h2>
            <p>
              Questions about these Terms: <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>.
            </p>
          </section>
        </article>

        <div className="mt-12 pt-6 border-t border-black/5 flex items-center justify-between text-sm">
          <Link to="/" className="underline text-[#1E392A]/70" data-testid="terms-home-link">← Back to home</Link>
          <Link to="/privacy" className="underline text-[#1E392A]/70" data-testid="terms-privacy-link">Privacy Policy →</Link>
        </div>
      </main>
    </div>
  );
}
